import {
  createContext,
  type ReactNode,
  type Ref,
  useContext,
  useId,
} from 'react';
import {
  Radio as AriaRadio,
  RadioGroup as AriaRadioGroup,
  type RadioGroupProps as AriaRadioGroupProps,
  type RadioProps as AriaRadioProps,
} from 'react-aria-components';

import type {
  SelectionCardSize,
  SelectionCardVariant,
} from '@/lib/base/forms/selection-card-shared';
import { selectionCardClassName } from '@/lib/base/forms/selection-card-shared';
import { cx } from '@/utils/cx';

export interface RadioGroupContextType {
  size?: 'sm' | 'md';
}

const RadioGroupContext = createContext<RadioGroupContextType | null>(null);

export interface RadioButtonBaseProps {
  size?: 'sm' | 'md';
  className?: string;
  isFocusVisible?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
}

export const RadioButtonBase = ({
  className,
  isFocusVisible,
  isSelected,
  isDisabled,
  size = 'sm',
}: RadioButtonBaseProps) => {
  return (
    <div
      className={cx(
        'flex size-4 min-h-4 min-w-4 cursor-pointer appearance-none items-center justify-center rounded-full bg-primary ring-1 ring-primary ring-inset',
        size === 'md' && 'size-5 min-h-5 min-w-5',
        isSelected && !isDisabled && 'bg-brand-solid ring-bg-brand-solid',
        isDisabled && 'cursor-not-allowed border-disabled bg-disabled_subtle',
        isFocusVisible && 'outline-2 outline-offset-2 outline-focus-ring',
        className
      )}
    >
      <div
        className={cx(
          'size-1.5 rounded-full bg-fg-white opacity-0 transition-inherit-all',
          size === 'md' && 'size-2',
          isDisabled && 'bg-fg-disabled_subtle',
          isSelected && 'opacity-100'
        )}
      />
    </div>
  );
};
RadioButtonBase.displayName = 'RadioButtonBase';

interface RadioButtonProps extends AriaRadioProps {
  size?: 'sm' | 'md';
  label?: ReactNode;
  hint?: ReactNode;
  /** Accessible helper text associated to the control via aria-describedby. Unlike `hint` (visual-only), this is announced by screen readers. */
  description?: ReactNode;
  ref?: Ref<HTMLLabelElement>;
}

export const RadioButton = ({
  label,
  hint,
  description,
  className,
  size = 'sm',
  ...ariaRadioProps
}: RadioButtonProps) => {
  const context = useContext(RadioGroupContext);
  const descId = useId();

  size = context?.size ?? size;

  const sizes = {
    sm: {
      root: 'gap-2',
      textWrapper: '',
      label: 'text-sm font-medium',
      hint: 'text-sm',
    },
    md: {
      root: 'gap-3',
      textWrapper: 'gap-0.5',
      label: 'text-md font-medium',
      hint: 'text-md',
    },
  };

  return (
    <AriaRadio
      {...ariaRadioProps}
      aria-describedby={description ? descId : undefined}
      className={renderProps =>
        cx(
          'flex items-start',
          renderProps.isDisabled && 'cursor-not-allowed',
          sizes[size].root,
          typeof className === 'function' ? className(renderProps) : className
        )
      }
    >
      {({ isSelected, isDisabled, isFocusVisible }) => (
        <>
          <RadioButtonBase
            size={size}
            isSelected={isSelected}
            isDisabled={isDisabled}
            isFocusVisible={isFocusVisible}
            className={label || hint || description ? 'mt-0.5' : ''}
          />
          {(label || hint || description) && (
            <div
              className={cx('inline-flex flex-col', sizes[size].textWrapper)}
            >
              {label && (
                <p
                  className={cx(
                    'text-secondary select-none',
                    sizes[size].label
                  )}
                >
                  {label}
                </p>
              )}
              {hint && (
                <span
                  className={cx('text-tertiary', sizes[size].hint)}
                  onClick={event => event.stopPropagation()}
                >
                  {hint}
                </span>
              )}
              {description && (
                <p
                  id={descId}
                  className={cx('text-tertiary', sizes[size].hint)}
                >
                  {description}
                </p>
              )}
            </div>
          )}
        </>
      )}
    </AriaRadio>
  );
};
RadioButton.displayName = 'RadioButton';

interface RadioGroupProps extends RadioGroupContextType, AriaRadioGroupProps {
  children: ReactNode;
  className?: string;
}

export const RadioGroup = ({
  children,
  className,
  size = 'sm',
  ...props
}: RadioGroupProps) => {
  return (
    <RadioGroupContext.Provider value={{ size }}>
      <AriaRadioGroup
        {...props}
        className={cx('flex flex-col gap-4', className)}
      >
        {children}
      </AriaRadioGroup>
    </RadioGroupContext.Provider>
  );
};

export interface RadioCardProps extends AriaRadioProps {
  /** Card contents (avatar, title, details, …). */
  children?: ReactNode;
  /**
   * When true, render a radio dot on the leading edge of the card (reflecting
   * the selected state) alongside the card contents. Defaults to false, in
   * which case selection is shown only via the brand-colored border. Use when
   * the card needs to read unambiguously as a "pick one" radio control.
   *
   * Ignored by the `'button'` variant, which has no indicator slot.
   */
  showIndicator?: boolean;
  /**
   * Visual treatment. `'card'` (default) is the bordered card; `'button'`
   * renders each option pixel-identical to a secondary Button. See
   * {@link selectionCardClassName}.
   */
  variant?: SelectionCardVariant;
  /** Card size. Only meaningful for the `'button'` variant. Defaults to 'md'. */
  size?: SelectionCardSize;
}

/**
 * A selectable card backed by a radio. The whole card is the click target. By
 * default the selected state is shown with a brand-colored border (no separate
 * radio circle); pass `showIndicator` to also render a radio dot. Use inside a
 * {@link RadioGroup} for any "pick one of N cards" UI.
 *
 * @example
 * <RadioGroup value={selectedId} onChange={setSelectedId} aria-label='Pick a plan'>
 *   <RadioCard value='a' showIndicator>…card content…</RadioCard>
 *   <RadioCard value='b' showIndicator>…card content…</RadioCard>
 * </RadioGroup>
 */
export const RadioCard = ({
  children,
  className,
  showIndicator = false,
  variant = 'card',
  size = 'md',
  ...ariaRadioProps
}: RadioCardProps) => {
  const withIndicator = variant === 'card' && showIndicator;

  return (
    <AriaRadio
      {...ariaRadioProps}
      className={renderProps =>
        cx(
          selectionCardClassName(variant, size, renderProps),
          // Direction only applies to the bordered card; the button variant
          // carries its own layout via the shared Button tokens.
          variant === 'card' &&
            (showIndicator ? 'flex-row items-start' : 'flex-col'),
          typeof className === 'function' ? className(renderProps) : className
        )
      }
    >
      {withIndicator
        ? renderProps => (
            <>
              <RadioButtonBase
                size='md'
                isSelected={renderProps.isSelected}
                isDisabled={renderProps.isDisabled}
                className='mt-0.5'
              />
              <div className='flex flex-1 flex-col gap-1'>{children}</div>
            </>
          )
        : children}
    </AriaRadio>
  );
};
RadioCard.displayName = 'RadioCard';
